import React from 'react';
import { CrewLookupListPanel } from '../crew-lookups/List';
import { CREW_LOOKUP_DEFS, type CrewLookupKind } from '../crew-lookups/registry';
import { List as CrewQuestionnairesList } from '../crew-questionnaires';
import { List as CrewWageColumnsList } from '../crew-wage-columns';
import { List as CrewWageScalesList } from '../crew-wage-scales';
import { List as CrewHolidaysList } from '../crew-holidays';
import ContractTermsPanel from '../crew-contract-terms/ContractTermsPanel';
import CrewMatrixRulesPanel from '../crew-manning-matrix/CrewMatrixRulesPanel';
import RankRequirementsPage from '../../crew/RankRequirementsPage';
import {
  ReferenceCategoryWorkspace,
  type ReferenceNavItem,
} from '../reference-workspaces/ReferenceCategoryWorkspace';

export type CrewReferenceSection =
  | CrewLookupKind
  | 'certificate-requirements'
  | 'questionnaires'
  | 'wage-columns'
  | 'wage-scales'
  | 'contract-terms'
  | 'crew-matrix-rules'
  | 'holidays';

const BASE = '/parameters/crew';

const lookupByKind = new Map(
  CREW_LOOKUP_DEFS.filter((d) => d.kind !== 'cert-categories').map((d) => [d.kind, d]),
);

function lookupItem(kind: CrewLookupKind, group: string): ReferenceNavItem {
  const def = lookupByKind.get(kind);
  if (!def) throw new Error(`Missing crew lookup ${kind}`);
  return {
    id: def.kind,
    label: def.label,
    description: def.singular,
    group,
    fillHeight: def.kind === 'cert-catalogs',
    permissionModuleKey: 'crew_parameters',
  };
}

function extraItem(
  item: Omit<ReferenceNavItem, 'permissionModuleKey' | 'group'> & { group: string },
): ReferenceNavItem {
  return { ...item, permissionModuleKey: 'crew_parameters' };
}

const ITEMS: ReferenceNavItem[] = [
  lookupItem('ranks', 'Seafarer'),
  lookupItem('nationalities', 'Seafarer'),
  lookupItem('sex-types', 'Seafarer'),
  lookupItem('marital-statuses', 'Seafarer'),
  lookupItem('relations', 'Seafarer'),
  lookupItem('religions', 'Seafarer'),
  lookupItem('availability-statuses', 'Seafarer'),
  lookupItem('cert-catalogs', 'Documents'),
  extraItem({
    id: 'certificate-requirements',
    label: 'Document requirements',
    description: 'Certificates by rank and nationality',
    group: 'Documents',
    fillHeight: true,
  }),
  lookupItem('manning-agents', 'Contracts'),
  lookupItem('employment-statuses', 'Contracts'),
  lookupItem('sign-off-reasons', 'Contracts'),
  extraItem({
    id: 'contract-terms',
    label: 'Contract terms',
    description: 'Contract months by nationality',
    group: 'Contracts',
    fillHeight: true,
  }),
  extraItem({
    id: 'questionnaires',
    label: 'Crew questionnaires',
    description: 'Questionnaire templates',
    group: 'Contracts',
    fillHeight: true,
  }),
  extraItem({
    id: 'wage-columns',
    label: 'Wage columns',
    description: 'Contract wage columns',
    group: 'Wages',
  }),
  extraItem({
    id: 'wage-scales',
    label: 'Wage scales',
    description: 'Rank wage scales',
    group: 'Wages',
  }),
  extraItem({
    id: 'holidays',
    label: 'Holidays',
    description: 'Crew holiday calendar',
    group: 'Wages',
  }),
  lookupItem('vessel-types', 'Experience'),
  lookupItem('engine-types', 'Experience'),
  lookupItem('vessel-owners', 'Experience'),
  lookupItem('ecdis-types', 'Experience'),
  extraItem({
    id: 'crew-matrix-rules',
    label: 'Crew Matrix Rules',
    description: 'Experience and pair rules for crew changes',
    group: 'Experience',
    fillHeight: true,
  }),
  lookupItem('travel-segment-types', 'Travel'),
];

function isLookupKind(section: string): section is CrewLookupKind {
  return CREW_LOOKUP_DEFS.some((d) => d.kind === section);
}

function renderPanel(section: string) {
  if (isLookupKind(section)) return <CrewLookupListPanel kind={section} />;
  if (section === 'questionnaires') return <CrewQuestionnairesList />;
  if (section === 'wage-columns') return <CrewWageColumnsList />;
  if (section === 'wage-scales') return <CrewWageScalesList />;
  if (section === 'contract-terms') return <ContractTermsPanel />;
  if (section === 'crew-matrix-rules') return <CrewMatrixRulesPanel />;
  if (section === 'certificate-requirements') {
    return (
      <div className="h-full min-h-0 p-4">
        <RankRequirementsPage />
      </div>
    );
  }
  if (section === 'holidays') return <CrewHolidaysList />;
  return null;
}

export function resolveCrewReferenceSection(raw: string | null | undefined): CrewReferenceSection {
  const ids = new Set(ITEMS.map((i) => i.id));
  if (raw && ids.has(raw)) return raw as CrewReferenceSection;
  return 'ranks';
}

const CrewReferenceWorkspace: React.FC = () => (
  <ReferenceCategoryWorkspace
    basePath={BASE}
    eyebrow="Human Resources"
    items={ITEMS}
    defaultSection="ranks"
    renderPanel={renderPanel}
  />
);

export default CrewReferenceWorkspace;
